/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useErrorMsg } from '@/hooks/useErrorMsg';
import { useUserProfile } from '@/hooks/useUserProfile';
import { IErrorType } from '@/pages/security/types';
import { IBaseResponse, UserInfo } from '@/services/types/common';
import { IWholeMobileAndEmailResult } from '@/services/types/verify';
import { getUserTag } from '@/services/user';
import { getWholeMobileOrEmail } from '@/services/verify';
import { SUB_ATTRIBUTE } from '@/utils/constants/compliance';

const UserContext = createContext({
  mutate: (callback: any) => {
    callback?.();
  },
  updateUserProfile: (cb?: (profile: any) => void) => {
    cb?.({});
  },
  accountIdentity: [] as string[],
  userProfile: {
    id: 0,
    userInfo: {} as UserInfo,
    profile: {} as IBaseResponse<UserInfo> | undefined,
  },
  profile: {} as any,
  loading: false,
  complianceConfig: {} as any,
  toggleWholeEmail: () => {},
  toggleWholePhoneNumber: () => {},
  wholeEmail: '',
  wholePhoneNumber: '',
  showWholeEmail: false,
  showWholePhoneNumber: false,
  msgError: (error: IErrorType) => {},
  RegulatedSiteHosts: '',
  queryUserTags: (tags: string[], forceUpdate?: boolean) =>
    ({} as { [key: string]: any }),
});

export const UserProvider = ({ children }: { children: any }) => {
  const {
    mutate,
    updateUserProfile,
    accountIdentity,
    loading,
    ...userProfile
  } = useUserProfile();

  const { msgError } = useErrorMsg(mutate);

  // 初始化逻辑
  const [complianceConfig, setComplianceConfig] = useState(
    window.byComplianceConfig?.initialConfig || {},
  );

  const [showWholePhoneNumber, setShowWholePhoneNumber] = useState(false);
  const [wholePhoneNumber, setWholePhoneNumber] = useState('');
  const [showWholeEmail, setShowWholeEmail] = useState(false);
  const [wholeEmail, setWholeEmail] = useState('');
  const [RegulatedSiteHosts, setRegulatedSiteHosts] = useState('');

  const [userTags, setUserTags] = useState<{ [key: string]: any }>({});

  const queryUserTags = async (tags: string[], forceUpdate = false) => {
    if (tags.every((tag) => userTags[tag]) && !forceUpdate) {
      const queryTags: { [key: string]: any } = {};
      tags.forEach((tag) => {
        queryTags[tag] = userTags[tag];
      });
      return queryTags;
    }
    const res = await getUserTag(tags);
    const newUserTags = res?.result?.user_tags || {};
    const copyUserTags = { ...userTags };
    tags.forEach((tag) => {
      copyUserTags[tag] = newUserTags[tag];
    });
    setUserTags(copyUserTags);
    return copyUserTags;
  };

  const getWholeMobileOrEmailFn = (params: {
    need_email: boolean;
    need_mobile: boolean;
  }) => {
    return getWholeMobileOrEmail(params)
      .then((res) => {
        const { email, mobile_with_country } =
          res as IWholeMobileAndEmailResult;
        setWholeEmail(email);
        setWholePhoneNumber(mobile_with_country);
      })
      .catch((err) => {
        msgError(err);
      });
  };

  useEffect(() => {
    if (userProfile.id) {
      getWholeMobileOrEmailFn({
        need_email: true,
        need_mobile: true,
      });
    }
  }, [userProfile]);

  const getRegulatedSiteHosts = useCallback(() => {
    if (typeof window !== 'undefined') {
      setRegulatedSiteHosts(window.RegulatedSiteHosts);
    } else {
      setTimeout(() => {
        getRegulatedSiteHosts();
      }, 300);
    }
  }, []);

  useEffect(() => {
    getRegulatedSiteHosts();
  }, [getRegulatedSiteHosts]);

  const toggleWholeEmail = async () => {
    setShowWholeEmail((showWholeEmail) => !showWholeEmail);
  };

  const toggleWholePhoneNumber = async () => {
    setShowWholePhoneNumber((showWholePhoneNumber) => !showWholePhoneNumber);
  };

  // 订阅更新逻辑
  useEffect(() => {
    // 订阅合规配置变化事件
    const unsubscribe = window.byComplianceConfig?.subscribe?.(
      (config: any) => {
        // 合规配置变化，重新setState
        setComplianceConfig(config);
      },
    );
    return unsubscribe;
  }, []);

  // 设置profile接口返回的sub_attribute，如果需要筛选伊斯兰子账号的配置，则可以像下面那样判断
  useEffect(() => {
    if (!loading && userProfile.id !== 0) {
      if (
        Object.values(SUB_ATTRIBUTE).includes(
          userProfile.userInfo.sub_attribute,
        )
      ) {
        window.byComplianceConfig?.setAccount?.(
          userProfile.userInfo.sub_attribute,
        );
      }
    }
  }, [loading, userProfile]);

  return (
    <UserContext.Provider
      value={{
        userProfile,
        profile: userProfile.profile?.result || {},
        accountIdentity,
        updateUserProfile,
        mutate,
        loading,
        complianceConfig,
        toggleWholeEmail,
        toggleWholePhoneNumber,
        wholeEmail,
        wholePhoneNumber,
        showWholeEmail,
        showWholePhoneNumber,
        msgError,
        RegulatedSiteHosts,
        queryUserTags,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserStore = () => useContext(UserContext);
